import { Injector } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  mapTo,
  pluck,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { CreateAccount } from 'src/app/accounts/interfaces/accounts.types';
import { AccountsService } from 'src/app/accounts/services/accounts.service';
import { MeService } from 'src/app/me/services/me.service';
import { RefResponse } from 'src/app/nipt/interfaces/ref-response.interface';
import { NiptApiService } from 'src/app/nipt/services/nipt-api.service';
import {
  OnboardingIntentStep,
  OnboardingType,
} from 'src/app/shared/enums/intent.enum';
import {
  IntentProperties,
  UserIntentState,
} from 'src/app/shared/interfaces/intent.interface';
import { environment } from 'src/environments/environment';
import { IntentService } from '../intent.service';
import { NIPT_STEPS } from '../steps';
import { IntentStrategy, IntentStrategyConfig } from './intent.strategy';
import { HttpStatusCode } from '@angular/common/http';

export class NiptIntentStrategy implements IntentStrategy {
  private intentService: IntentService;
  private router: Router;
  private activatedRoute: ActivatedRoute;
  private niptApiService: NiptApiService;
  private accountsService: AccountsService;
  private meService: MeService;

  constructor(
    private injector: Injector,
    private ref?: string
  ) {
    this.intentService = this.injector.get(IntentService);
    this.router = this.injector.get(Router);
    this.activatedRoute = this.injector.get(ActivatedRoute);
    this.niptApiService = this.injector.get(NiptApiService);
    this.accountsService = this.injector.get(AccountsService);
    this.meService = this.injector.get(MeService);
  }

  public getConfig(): IntentStrategyConfig {
    const PREFIX = 'ONBOARDING.NIPT.';

    return {
      steps: NIPT_STEPS,
      type: OnboardingType.NIPT,
      [OnboardingIntentStep.Welcome]: {
        title: PREFIX + 'WELCOME.WELCOME',
        description: PREFIX + 'WELCOME.YOU_ARE_ABOUT_TO_BEGIN',
      },
      [OnboardingIntentStep.Caution]: {
        paragraph1: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_1',
        paragraph2: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_2',
      },
    };
  }

  public onAppInit(milestone: OnboardingIntentStep): void {
    this.redirectToMilestone(milestone);
  }

  public onWelcomeInit(forceInit: boolean): Observable<any> {
    if (
      !this.intentService.isUserInIntent(this.intentService.state$.value) ||
      forceInit
    ) {
      const ref = this.getNiptRef();
      if (!ref) {
        return of({}).pipe(
          tap(() => this.router.navigate(['/accounts/check']))
        );
      }

      return this.niptApiService.getRefData(ref).pipe(
        take(1),
        switchMap((data: RefResponse) => {
          if (data?.claimed) {
            return of({}).pipe(
              tap(() => {
                this.router.navigate([`/nipt/results/${data.testId}`]);
              })
            );
          } else if (data?.email) {
            return this.intentService
              .startIntent(environment.intents.nipt)
              .pipe(
                switchMap(() =>
                  this.onNextStep(OnboardingIntentStep.Welcome, { ref })
                )
              );
          } else {
            return of({}).pipe(
              tap(() => this.router.navigate(['/accounts/check']))
            );
          }
        }),
        catchError((error) =>
          // BE returns 400 Bad Request when ref doesn't exist
          of({}).pipe(
            tap(() => {
              if (error.status === HttpStatusCode.NotFound) {
                this.router.navigate(['/nipt/nipt-expired']);
              } else {
                this.router.navigate(['/accounts/check']);
              }
            })
          )
        )
      );
    }

    return of({});
  }

  public onWelcomeLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onWelcomeCreateAccount(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onRegisterCreateAccount({
    email,
    password,
  }: CreateAccount): Observable<any> {
    const ref = this.getNiptRef();
    if (ref) {
      return this.accountsService
        .createNipt(email, password, ref)
        .pipe(
          tap(() =>
            this.onNextStep(OnboardingIntentStep.Newsletter).subscribe()
          )
        );
    }
    return of({}).pipe(tap(() => this.router.navigate(['/accounts/check'])));
  }

  public onRegisterLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onNewsletterNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Caution).subscribe();
  }

  public onCautionNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Onboarding).subscribe();
  }

  public onNotValidatedEmail(): Observable<UrlTree> {
    return of(this.router.parseUrl('/accounts/verify'));
  }

  public onVerifyGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onEmailActivation(): Observable<boolean> {
    if (
      this.intentService.state$?.value?.milestoneState ===
      OnboardingIntentStep.Login
    ) {
      of(true);
    }

    return this.onNextStep(OnboardingIntentStep.Login).pipe(mapTo(false));
  }

  public onSkipEmailActivation(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  // Should be unused (#7935)
  public onDownloadAfterCheckSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.Onboarding).subscribe();
  }

  public onAlreadyActivitedEmailGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onLoginRegister(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onLoginSuccessfully(): void {
    this.onNextStep(OnboardingIntentStep.Onboarding).subscribe();
  }

  public onCreateCustomerSuccessfully(): void {
    this.onNextStep(OnboardingIntentStep.Claim).subscribe();
  }

  // Should be unused (#7935)
  public onTrackSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.Claim).subscribe();
  }

  public onNiptClaimInit(): Observable<any> {
    const ref = this.getNiptRef();
    const profile$ = this.meService.getFullProfile()!.pipe(take(1));
    const refData$ = this.niptApiService.getRefData(ref);

    return combineLatest([profile$, refData$]).pipe(
      switchMap(([me, data]) => {
        if (me.email.toLowerCase() !== data.email.toLowerCase()) {
          this.intentService.cancelIntent().subscribe(() => {
            this.meService.logout();

            this.router.navigate([`/nipt/view`, ref]);
          });
        }

        return data?.claimed
          ? this.onNextStep(OnboardingIntentStep.End)
          : of({});
      }),
      catchError((error) => {
        if (error.status === HttpStatusCode.NotFound) {
          this.router.navigate(['/nipt/nipt-expired']);
        }
        return of(error);
      })
    );
  }

  public onNiptClaimActiveClick(barcode: string): Observable<any> {
    const ref = this.getNiptRef();

    return this.niptApiService.getRefData(ref).pipe(
      switchMap((data) => {
        return this.niptApiService
          .claimKit(data.testId, barcode)
          .pipe(switchMap(() => this.onNextStep(OnboardingIntentStep.End)));
      })
    );
  }

  public onCustomerNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  public onCustomerExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Claim).pipe(mapTo(false));
  }

  public onCustomerWelcomeExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Welcome).pipe(map(() => false));
  }

  public onUserNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Login).pipe(mapTo(false));
  }

  private onNextStep(
    milestone?: OnboardingIntentStep,
    properties?: IntentProperties
  ): Observable<UserIntentState | unknown> {
    return this.intentService
      .nextStep(milestone, properties)
      .pipe(
        tap((state) =>
          this.redirectToMilestone((state as UserIntentState).milestoneState!)
        )
      );
  }

  private redirectToMilestone(milestone: OnboardingIntentStep) {
    const { steps } = this.getConfig();
    const { path, state, queryParams } = steps![milestone];

    if (milestone === OnboardingIntentStep.End) {
      this.getTestIdByRef(this.getNiptRef()).subscribe((testId) => {
        this.router.navigate([`${path}/${testId}`], {
          state,
          queryParams,
          queryParamsHandling: 'preserve',
        });
      });

      this.intentService.cancelIntent().subscribe();
    } else {
      this.router.navigate([path], {
        state,
        queryParams,
        queryParamsHandling: 'preserve',
      });
    }
  }

  private getNiptRef(): string {
    const ref =
      this.ref ||
      this.intentService.state$.value?.properties?.ref ||
      this.activatedRoute.snapshot.queryParams.ref;

    if (!ref) {
      if (this.intentService.isUserInIntent(this.intentService.state$.value)) {
        this.intentService.cancelIntent().subscribe(() => {
          location.reload();
        });
      }
    }

    return ref;
  }

  private getTestIdByRef(ref: string): Observable<string> {
    return this.niptApiService.getRefData(ref).pipe(pluck('testId'));
  }
}
